import { useContext, useLayoutEffect, type ReactNode } from "react";
import { Command as CommandPrimitive, useCommandState } from "cmdk";
import { useTranslation } from "react-i18next";

import { SelectContentContext, useSelectContext } from "./SelectContext";

/** Configures the `Select`'s `creator` row, which offers the typed search text as a new option. */
export interface SelectCreator<TValue extends string = string> {
  /** The row's content for the typed text. Defaults to `Create {{text}}`. */
  label?: (text: string) => ReactNode;
  /** Gate on the trimmed text; the row only shows when this returns true. */
  isValid?: (text: string) => boolean;
  /** Maps the trimmed text to the value that gets selected. Defaults to the text itself. */
  transform?: (text: string) => TValue;
  /** Called with the trimmed text after the created value is selected. */
  onCreate?: (text: string) => void;
  /** Commit the typed text when the dropdown closes (click outside, Tab) while the row is showing. Escape still cancels. */
  commitOnClose?: boolean;
}

/** The "create" row itself, shown only while the search text is new and valid. */
export const SelectCreateRow = ({ label, isValid, transform, onCreate, commitOnClose }: SelectCreator) => {
  const { t } = useTranslation();
  const { hasItemMatching, isSelected, selectItem, rememberItem } = useSelectContext();
  const content = useContext(SelectContentContext);
  const text = useCommandState((state) => state.search).trim();
  const visible = !!text && !hasItemMatching(text) && (isValid?.(text) ?? true);

  const commit = () => {
    const createdValue = transform?.(text) ?? text;
    rememberItem(createdValue, { node: text, textValue: text });
    if (!isSelected(createdValue)) selectItem(createdValue);
    onCreate?.(text);
    content?.setSearch("");
  };

  // Hands the latest `commit` to the content's close handler; refs can't be written during render.
  // No cleanup on purpose: Radix fires `onCloseAutoFocus` in a timeout after this row has unmounted,
  // so the commit must outlive it (the open handler resets it instead).
  useLayoutEffect(() => {
    if (content) content.pendingCommit.current = visible && commitOnClose ? commit : null;
  });

  if (!visible) return null;

  return (
    <CommandPrimitive.Item
      data-slot="select-create"
      forceMount
      value={`select-create:${text}`}
      onSelect={commit}
      className="relative flex w-full cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none select-none data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground"
    >
      {label ? label(text) : t("Create {{text}}", { text })}
    </CommandPrimitive.Item>
  );
};
